/** @jsxImportSource theme-ui */
import { ThemeUIStyleObject, Box, get, Flex } from 'theme-ui';
import React, { useState } from 'react';
import { NavLink, useMatch, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/outline';
import { useTheme } from '@emotion/react';

interface Props extends React.HTMLAttributes<HTMLElement> {
    to?: string;
    label: React.ReactNode;
    sx?: ThemeUIStyleObject;
    icon?: React.ReactElement[];
    isNavLinkAware?: boolean;
    collapsed?: boolean;
}

const CustomLink: React.FC<Props> = ({
    children,
    to,
    label,
    icon,
    isNavLinkAware = true,
    collapsed = false,
    sx = {},
    ...rest
}) => {
    const theme = useTheme();
    const match = useMatch(to ?? '');
    const location = useLocation();
    const extendedMatch = match && match?.pathname !== '/';

    const Wrapper: any = to ? NavLink : Flex;

    const anyChildMatches = React.Children.toArray(children)
        .map((child) => {
            if (!React.isValidElement(child)) return undefined;
            return (child.props as any)?.to;
        })
        .some((childTo) => childTo === location.pathname);

    const [folded, setFolded] = useState(!anyChildMatches);

    return (
        <Flex
            sx={{
                flexDirection: 'column',
                flexGrow: 1,
                borderRadius: 'sm',
                backgroundColor: anyChildMatches ? get(theme, 'colors.gray.1') : undefined,
                '& > *:not(.active)': {
                    backgroundColor: anyChildMatches ? get(theme, 'colors.gray.1') : undefined,
                },
                overflow: 'hidden',
            }}
        >
            <Wrapper
                {...(to ? { to } : {})}
                sx={{
                    ...(React.Children.count(children) > 0
                        ? { cursor: 'pointer', userSelect: 'none' }
                        : {}),
                    borderRadius: 'xsm',
                    mb: 3,
                    textDecoration: 'none',
                    fontFamily: get(theme, 'links.default.fontFamily'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: collapsed ? 'center' : undefined,
                    backgroundColor:
                        isNavLinkAware && extendedMatch ? get(theme, 'colors.primary') : undefined,
                    color:
                        isNavLinkAware && extendedMatch
                            ? get(theme, 'colors.background')
                            : get(theme, 'colors.gray.9'),

                    ...sx,
                }}
                onClick={() => setFolded(!folded)}
                {...rest}
            >
                <Flex sx={{ justifyContent: 'center', svg: { margin: 0 } }}>
                    {React.Children.map(icon, (child, i) => {
                        if (i > 0 && extendedMatch) {
                            return child;
                        }
                        if (i === 0 && !extendedMatch) {
                            return child;
                        }
                        return null;
                    })}
                    {!icon ? <Box mr="2" sx={{ height: 12 }} /> : null}
                </Flex>
                {!collapsed ? <Box as="span">{label}</Box> : null}
                {React.Children.count(children) > 0 && folded ? (
                    <span sx={{ marginLeft: 'auto' }}>
                        <ChevronRightIcon
                            width={16}
                            height={16}
                            sx={{
                                color: 'gray.9',
                                mr: 2,
                                transform: 'rotate(90deg)',
                                transition: '0.2s all',
                            }}
                        />
                    </span>
                ) : React.Children.count(children) > 0 && !folded ? (
                    <span sx={{ marginLeft: 'auto' }}>
                        <ChevronRightIcon
                            width={16}
                            height={16}
                            sx={{ color: 'gray.9', mr: 2, transition: '0.2s all' }}
                        />
                    </span>
                ) : null}
            </Wrapper>
            {!folded &&
                React.Children.map(children, (child, i) => {
                    if (!React.isValidElement(child)) return null;
                    return (
                        <Box key={i} pl="4" pr="2">
                            {React.cloneElement(child)}
                        </Box>
                    );
                })}
        </Flex>
    );
};

export default CustomLink;
